'use client';
import { HStack } from '@chakra-ui/react';
import cn from 'classnames';
import Link from 'next/link';
import React, { useEffect } from 'react';
import { useRecoilState } from 'recoil';

import { useRoutesConfig } from '@/hooks/routes-config';
import { currentPagePath } from '@/states/home/quick-search.state';

import styles from './breadcrumbs.module.scss';

interface Item {
  path: string;
  name: string | null;
}

const CompactLinks = ({ items }: { items: Item[] }) => {
  return items.map((item, index) => {
    const lastIndex = items.length - 1;
    const isActive = index === lastIndex;
    const parentIndex = lastIndex - 1;

    return (
      <React.Fragment key={index}>
        {index == parentIndex ? (
          <Link
            href={item.path.toLowerCase()}
            key={item.name}
            className={cn(styles.item, {
              [styles.itemActive as string]: isActive,
            })}
          >
            &lt; Back to {item.name}
          </Link>
        ) : null}
      </React.Fragment>
    );
  });
};

const FullSizeLinks = ({ items }: { items: Item[] }) => {
  return items.map((item, index) => {
    const isActive = index === items.length - 1;

    return (
      <React.Fragment key={index}>
        {index > 0 && <div className={styles.itemSeparator}>/</div>}
        <Link
          href={item.path.toLowerCase()}
          key={item.name}
          className={cn(styles.item, {
            [styles.itemActive as string]: isActive,
          })}
        >
          {item.name}
        </Link>
      </React.Fragment>
    );
  });
};

export const Breadcrumbs: React.FC<{ compact?: boolean; items: Item[] }> = ({
  compact = false,
  items,
}) => {
  const routes = useRoutesConfig();
  const [, setCurrentPagePath] = useRecoilState(currentPagePath);

  useEffect(() => {
    if (items[0]?.path === routes.usedCars.path) {
      setCurrentPagePath(routes.usedCars.path);
    } else {
      items[0]?.path && setCurrentPagePath(items[0].path);
    }

    return () => {
      setCurrentPagePath(null);
    };
  }, [items]);

  return (
    <HStack spacing={0.5} className={styles.wrapper}>
      <Link
        aria-label="Kia Home"
        href={routes.root.path}
        className={styles.homeItem}
      >
        <div className={cn(styles.homeIcon, styles.item)}></div>
      </Link>
      {compact ? (
        <CompactLinks items={items} />
      ) : (
        <FullSizeLinks items={items} />
      )}
    </HStack>
  );
};
